import React, { useEffect, useState, useCallback } from 'react';
import { useApp } from '../../context/AppContext';
import { Input } from '../../components/ui/input';
import { BadgePercent, Trash2, Plus, Power } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const S = {
  h1: { fontFamily: 'Cormorant Garamond, serif', fontSize: '28px', fontWeight: 600, color: '#1F3A2E' },
  card: { background: 'white', borderRadius: '14px', border: '1px solid #EFE7DA', padding: '18px 20px', marginBottom: '16px' },
  label: { fontSize: '11px', fontWeight: 700, color: '#6E7F5B', textTransform: 'uppercase', letterSpacing: '0.05em', display: 'block', marginBottom: '5px' },
  input: { border: '1px solid #D8D1C6', borderRadius: '9px', fontSize: '13px' },
  btn: (bg, color, border) => ({ display: 'inline-flex', alignItems: 'center', gap: '6px', background: bg, color, border: border || 'none', fontSize: '13px', fontWeight: 700, padding: '10px 18px', borderRadius: '10px', cursor: 'pointer', fontFamily: 'Manrope, sans-serif' }),
};

export default function AdminDiscounts() {
  const { apiHeaders, language, BACKEND_URL } = useApp();
  const [rooms, setRooms] = useState([]);
  const [discounts, setDiscounts] = useState([]);
  const [saving, setSaving] = useState(false);
  const emptyForm = { name: '', percent: 10, start_date: '', end_date: '', room_ids: [], active: true };
  const [form, setForm] = useState(emptyForm);

  const fetchAll = useCallback(async () => {
    try {
      const [r, d] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/rooms`),
        axios.get(`${BACKEND_URL}/api/admin/discounts`, apiHeaders()),
      ]);
      setRooms(r.data);
      setDiscounts(d.data);
    } catch { toast.error('Error'); }
  }, [BACKEND_URL]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const toggleRoom = (id) => {
    setForm(f => ({ ...f, room_ids: f.room_ids.includes(id) ? f.room_ids.filter(r => r !== id) : [...f.room_ids, id] }));
  };

  const handleCreate = async () => {
    if (!form.name.trim() || !form.start_date || !form.end_date) {
      toast.error(language === 'tr' ? 'Ad, başlangıç ve bitiş tarihi zorunlu' : 'Name, start and end date required');
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${BACKEND_URL}/api/admin/discounts`, { ...form, percent: parseFloat(form.percent) }, apiHeaders());
      toast.success(language === 'tr' ? 'İndirim oluşturuldu' : 'Discount created');
      setForm(emptyForm);
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
    setSaving(false);
  };

  const handleToggleActive = async (d) => {
    try {
      await axios.put(`${BACKEND_URL}/api/admin/discounts/${d._id}`, { name: d.name, percent: d.percent, start_date: d.start_date, end_date: d.end_date, room_ids: d.room_ids || [], active: !d.active }, apiHeaders());
      toast.success(d.active ? (language === 'tr' ? 'İndirim durduruldu' : 'Discount paused') : (language === 'tr' ? 'İndirim aktifleştirildi' : 'Discount activated'));
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(language === 'tr' ? 'Bu indirimi silmek istediğinize emin misiniz?' : 'Delete this discount?')) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/discounts/${id}`, apiHeaders());
      toast.success(language === 'tr' ? 'İndirim silindi' : 'Discount deleted');
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
  };

  const roomNames = (ids) => {
    if (!ids || ids.length === 0) return language === 'tr' ? 'Tüm Odalar' : 'All Rooms';
    return ids.map(id => rooms.find(r => r._id === id)?.name || '?').join(', ');
  };

  const today = new Date().toISOString().slice(0, 10);
  const statusOf = (d) => {
    if (!d.active) return { text: language === 'tr' ? 'Pasif' : 'Inactive', bg: '#e5e7eb', color: '#6b7280' };
    if (d.end_date < today) return { text: language === 'tr' ? 'Süresi Doldu' : 'Expired', bg: '#fef3c7', color: '#92400e' };
    if (d.start_date > today) return { text: language === 'tr' ? 'Planlandı' : 'Scheduled', bg: '#dbeafe', color: '#1d4ed8' };
    return { text: language === 'tr' ? '🔥 Yayında' : '🔥 Live', bg: '#dcfce7', color: '#166534' };
  };

  return (
    <div style={{ fontFamily: 'Manrope, sans-serif' }} data-testid="admin-discounts-page">
      <div style={{ marginBottom: '20px' }}>
        <h1 style={S.h1}>{language === 'tr' ? 'İndirim Yönetimi' : 'Discount Management'}</h1>
        <p style={{ fontSize: '13px', color: '#6E7F5B', marginTop: '2px' }}>
          {language === 'tr'
            ? 'Tarih aralığına özel indirim oluşturun. İndirim, oda kartlarında dikkat çekici rozet olarak görünür ve fiyata otomatik yansır.'
            : 'Create date-range discounts. They appear as flash badges on room cards and are applied to prices automatically.'}
        </p>
      </div>

      {/* Create form */}
      <div style={S.card} data-testid="discount-create-card">
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '12px', marginBottom: '14px' }}>
          <div style={{ gridColumn: 'span 2' }}>
            <label style={S.label}>{language === 'tr' ? 'İndirim Adı' : 'Discount Name'}</label>
            <Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder={language === 'tr' ? 'Örn: Bayrama Özel' : 'e.g. Holiday Special'} style={S.input} data-testid="discount-name-input" />
          </div>
          <div>
            <label style={S.label}>{language === 'tr' ? 'İndirim (%)' : 'Discount (%)'}</label>
            <Input type="number" min="1" max="90" value={form.percent} onChange={e => setForm(f => ({ ...f, percent: e.target.value }))} style={S.input} data-testid="discount-percent-input" />
          </div>
          <div>
            <label style={S.label}>{language === 'tr' ? 'Başlangıç' : 'Start Date'}</label>
            <Input type="date" value={form.start_date} onChange={e => setForm(f => ({ ...f, start_date: e.target.value }))} style={S.input} data-testid="discount-start-input" />
          </div>
          <div>
            <label style={S.label}>{language === 'tr' ? 'Bitiş' : 'End Date'}</label>
            <Input type="date" value={form.end_date} onChange={e => setForm(f => ({ ...f, end_date: e.target.value }))} style={S.input} data-testid="discount-end-input" />
          </div>
        </div>
        <label style={S.label}>{language === 'tr' ? 'Geçerli Odalar (boş bırakılırsa tüm odalar)' : 'Applicable Rooms (empty = all rooms)'}</label>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '14px' }}>
          <button type="button" onClick={() => setForm(f => ({ ...f, room_ids: [] }))}
            style={{ padding: '7px 14px', borderRadius: '18px', border: '1.5px solid', fontWeight: 600, fontSize: '12px', cursor: 'pointer', fontFamily: 'Manrope, sans-serif', background: form.room_ids.length === 0 ? '#1F3A2E' : 'white', color: form.room_ids.length === 0 ? 'white' : '#1F3A2E', borderColor: form.room_ids.length === 0 ? '#1F3A2E' : '#D8D1C6' }}
            data-testid="discount-all-rooms-button">
            {language === 'tr' ? 'Tüm Odalar' : 'All Rooms'}
          </button>
          {rooms.map(r => (
            <button key={r._id} type="button" onClick={() => toggleRoom(r._id)}
              style={{ padding: '7px 14px', borderRadius: '18px', border: '1.5px solid', fontWeight: 600, fontSize: '12px', cursor: 'pointer', fontFamily: 'Manrope, sans-serif', background: form.room_ids.includes(r._id) ? '#B08D57' : 'white', color: form.room_ids.includes(r._id) ? 'white' : '#1F3A2E', borderColor: form.room_ids.includes(r._id) ? '#B08D57' : '#D8D1C6' }}
              data-testid={`discount-room-${r._id}`}>
              {r.name}
            </button>
          ))}
        </div>
        <button onClick={handleCreate} disabled={saving} style={S.btn('#1F3A2E', 'white')} data-testid="discount-create-button">
          <Plus size={15} /> {language === 'tr' ? 'İndirim Oluştur' : 'Create Discount'}
        </button>
      </div>

      {/* List */}
      {discounts.length === 0 ? (
        <div style={{ ...S.card, textAlign: 'center', color: '#6E7F5B', fontSize: '14px', padding: '40px' }}>
          <BadgePercent size={32} style={{ margin: '0 auto 10px', opacity: 0.4 }} />
          {language === 'tr' ? 'Henüz indirim tanımlanmadı.' : 'No discounts defined yet.'}
        </div>
      ) : discounts.map(d => {
        const st = statusOf(d);
        return (
          <div key={d._id} style={{ ...S.card, display: 'flex', alignItems: 'center', gap: '14px', flexWrap: 'wrap' }} data-testid={`discount-row-${d._id}`}>
            <div style={{ width: '54px', height: '54px', borderRadius: '12px', background: 'linear-gradient(135deg, #dc2626, #f97316)', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: '15px', flexShrink: 0 }}>
              %{d.percent}
            </div>
            <div style={{ flex: 1, minWidth: '180px' }}>
              <p style={{ fontWeight: 700, fontSize: '15px', color: '#1F3A2E' }}>{d.name}</p>
              <p style={{ fontSize: '12px', color: '#6E7F5B', marginTop: '2px' }}>
                {d.start_date} → {d.end_date} · {roomNames(d.room_ids)}
              </p>
            </div>
            <span style={{ fontSize: '11px', fontWeight: 700, padding: '5px 12px', borderRadius: '14px', background: st.bg, color: st.color }}>{st.text}</span>
            <button onClick={() => handleToggleActive(d)} style={S.btn('white', d.active ? '#92400e' : '#166534', '1px solid #D8D1C6')} data-testid={`discount-toggle-${d._id}`}>
              <Power size={13} /> {d.active ? (language === 'tr' ? 'Durdur' : 'Pause') : (language === 'tr' ? 'Aktifleştir' : 'Activate')}
            </button>
            <button onClick={() => handleDelete(d._id)} style={S.btn('white', '#dc2626', '1px solid #fecaca')} data-testid={`discount-delete-${d._id}`}>
              <Trash2 size={13} />
            </button>
          </div>
        );
      })}
    </div>
  );
}
